<template>
  <div class="c-popularSubCategories">
    <ContentHeadline
      class="c-popularSubCategories_headline"
      title-tag="h2"
      titleText="人気のカテゴリ"
    />
    <div class="c-popularSubCategories_listWrapper">
      <div class="c-popularSubCategories_list">
        <NuxtLink
          v-for="subCategory in filteredSubCategories"
          class="c-popularSubCategories_item c-popularSubCategory"
          :key="`subCategory-${subCategory.id}`"
          :to="addQueryToUrlIfBusinessPreview(`/categories/${subCategory.id}?ref=${linkRef}`)"
        >
          <img
            class="c-popularSubCategory_image"
            :src="getCategoryImage(subCategory.id)"
            :alt="subCategory.name"
            loading="lazy"
            decoding="async"
          >
          <div class="c-popularSubCategory_name">
            <span
              v-for="(splited, idx) in splitName(subCategory)"
              class="c-popularSubCategory_splitedName"
              :key="`splited-name-${idx}`"
            >
              {{ splited }}
            </span>
          </div>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script>
import ContentHeadline from '~/components/molecules/ContentHeadline'
import LinkRefProvider from '~/mixins/link-ref/LinkRefProvider'
import businessMixin from '~/mixins/pages/business-mixin'

import { mapState, mapGetters } from 'vuex'

/** @type {import('vue').ComponentOptions} */
const popularSubCategories = {
  name: 'PopularSubCategories',
  mixins: [LinkRefProvider, businessMixin],
  components: {
    ContentHeadline
  },
  props: {
    subCategories: {
      type: Array,
      required: true
    },
    categoryId: {
      type: Number,
      default: null
    }
  },
  computed: {
    ...mapState('auth/user', ['isBusiness']),
    ...mapGetters('master', ['flattenMasterCategories']),
    filteredSubCategories() {
      if (!this.isBusiness && !this.$route.query.is_business) {
        return this.subCategories
      }

      // ビジネスモードだが表示しているのがビジネスカテゴリでない場合（URL直アクセス）、ビジネス対象カテゴリに絞らない
      const isNotBusinessCategory = this.flattenMasterCategories.some(
        category => category.id === this.categoryId && !category.businessFlag
      )
      if (isNotBusinessCategory) {
        return this.subCategories
      }

      // ビジネスモードのとき、ビジネス対象カテゴリのみ表示する
      return this.subCategories.filter(subCategory =>
        this.flattenMasterCategories.some(
          category => category.id === subCategory.id && category.businessFlag
        )
      )
    }
  },
  methods: {
    /**
     * カテゴリ画像取得
     * @param {number} categoryId カテゴリID
     */
    getCategoryImage(categoryId) {
      // TODO Cache busting
      return (
        process.env.config.coconalaWeb['image-url'] +
        `/staticfiles/images/categories/popular/category_${categoryId}.jpg`
      )
    },
    /**
     * カテゴリ名を分割
     * CSSのword-break: keep-allではうまく改行されないブラウザがあるためにこちらで対応
     * 例）"労務・社会保険（社会保険労務士）" -> ["労務・", "社会保険", "（社会保険労務士）"]
     * @param {string} name カテゴリ名
     * @param {number} id カテゴリID
     */
    splitName({ name, id }) {
      // 分割すると3行になってしまうカテゴリのイレギュラー対応
      if ([629, 665, 678].includes(id)) {
        return [name]
      }
      /** 括弧含むパターン */
      const bracketsPattern = /^(.+)(（.+）)$/
      if (bracketsPattern.test(name)) {
        return (
          name
            // 文字列の中に","が含まれる可能性を考慮して",,"で分割
            .replace(bracketsPattern, '$1,,$2')
            .split(',,')
            // 分割した文字列の中に"・"が含まれることもあるので再帰的に変換かける
            .map(x => this.splitName({ name: x }))
            .reduce((a, b) => a.concat(b))
        )
      }
      const splitedName = name.split('・').map((x, i, a) => {
        if (i < a.length - 1) {
          return `${x}・`
        }
        return x
      })
      return splitedName
    }
  }
}
export default popularSubCategories
</script>

<style lang="scss" scoped>
// 画像アスペクト比
$image-aspect-ratio: 119 / 227;

.c-popularSubCategories {
  &_list {
    display: flex;

    flex-wrap: wrap;
  }
  &_item {
    margin-top: 16px;
    margin-right: 16px;
    &:nth-child(4n) {
      margin-right: 0;
    }
  }
}
.c-popularSubCategory {
  @include link-effect;

  display: block;
  overflow: hidden;
  width: 229px;
  border: 1px solid $color-gray-200;
  border-radius: 5px;
  background-color: $color-white;
  box-shadow: 0 1px 1px rgba($color-black, 0.16), 0 0.5px 2px rgba($color-black, 0.12);
  &_image {
    display: block;
    width: 227px;
    height: 227px * $image-aspect-ratio;
  }
  &_name {
    display: flex;
    padding: 0 8px;
    height: 56px;
    color: $ds2-color-gray-900;
    text-align: center;
    font-weight: bold;
    font-size: 14px;
    line-height: 1.3;

    align-content: center;
    align-items: center;
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media (max-width: breakpoint(Search, XL) - 1px) {
  .c-popularSubCategories {
    &_item {
      margin-right: 12px;
      &:nth-child(4n) {
        margin-right: 12px;
      }
      &:nth-child(3n) {
        margin-right: 0;
      }
    }
  }
}
@media (max-width: breakpoint(Search, M)) {
  .c-popularSubCategories {
    &_headline {
      padding-left: 12px;
    }
    &_listWrapper {
      overflow: hidden;
      margin-top: 8px;
      width: 100%;
    }
    &_list {
      overflow-x: auto;
      // 子要素のbox-shadow分
      padding-bottom: 2px;
      width: 100%;

      flex-wrap: nowrap;
      -ms-overflow-style: none;
      scrollbar-width: none;
      &::after {
        display: block;
        padding-left: 4px;
        content: '';
      }
      &::-webkit-scrollbar {
        display: none;
      }
    }
    &_item {
      margin-top: 0;
      margin-right: 8px;

      flex-shrink: 0;
      &:nth-child(n) {
        margin-right: 8px;
      }
      &:first-child {
        margin-left: 12px;
      }
    }
  }
  .c-popularSubCategory {
    width: 138px;
    border: 0;
    &_image {
      width: 138px;
      height: 138px * $image-aspect-ratio;
    }
    &_name {
      height: 48px;
      font-weight: 700;
      font-size: 12px;
    }
    &_splitedName {
      max-width: 100%;

      flex-grow: 0;
      flex-shrink: 0;
    }
  }
}
</style>
